import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Calendar, MessageSquare, Loader2, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/${activeTab}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const downloadCSV = () => {
    let csvContent = '';
    
    if (activeTab === 'subscriptions') {
      csvContent = 'Email,Subscribed At,Status\n';
      data.forEach(item => {
        csvContent += `${item.email},${formatDate(item.subscribed_at)},${item.status}\n`;
      });
    } else if (activeTab === 'contacts') {
      csvContent = 'Name,Email,Phone,Message,Submitted At\n';
      data.forEach(item => {
        csvContent += `${item.name},${item.email},${item.phone || 'N/A'},"${item.message}",${formatDate(item.submitted_at)}\n`;
      });
    } else if (activeTab === 'appointments') {
      csvContent = 'Name,Email,Phone,Date,Time,Notes,Booked At\n';
      data.forEach(item => {
        csvContent += `${item.name},${item.email},${item.phone || 'N/A'},${formatDate(item.date)},${item.time},"${item.notes || 'N/A'}",${formatDate(item.booked_at)}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <div className="luxury-container" style={{ padding: '60px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
          <button 
            onClick={() => navigate('/')}
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
          
          <button
            onClick={downloadCSV}
            className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Download size={16} />
            Export to CSV
          </button>
        </div>

        <h1 className="heading-1" style={{ marginBottom: '40px' }}>Admin Dashboard</h1>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '40px', borderBottom: '2px solid var(--border-light)' }}>
          <button
            onClick={() => setActiveTab('subscriptions')}
            style={{
              padding: '16px 24px',
              background: activeTab === 'subscriptions' ? 'var(--bg-secondary)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'subscriptions' ? '2px solid var(--accent-gold)' : 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: activeTab === 'subscriptions' ? '600' : '400',
              marginBottom: '-2px'
            }}
          >
            <Mail size={18} />
            Email Subscriptions ({data.length})
          </button>
          
          <button
            onClick={() => setActiveTab('contacts')}
            style={{
              padding: '16px 24px',
              background: activeTab === 'contacts' ? 'var(--bg-secondary)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'contacts' ? '2px solid var(--accent-gold)' : 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: activeTab === 'contacts' ? '600' : '400',
              marginBottom: '-2px'
            }}
          >
            <MessageSquare size={18} />
            Contact Submissions ({data.length})
          </button>
          
          <button
            onClick={() => setActiveTab('appointments')}
            style={{
              padding: '16px 24px',
              background: activeTab === 'appointments' ? 'var(--bg-secondary)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'appointments' ? '2px solid var(--accent-gold)' : 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: activeTab === 'appointments' ? '600' : '400',
              marginBottom: '-2px'
            }}
          >
            <Calendar size={18} />
            Appointments ({data.length})
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
            <Loader2 size={40} className="animate-spin" color="var(--accent-gold)" />
          </div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
            <p>No {activeTab} yet</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {activeTab === 'subscriptions' && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border-light)' }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Email</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Subscribed At</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{item.email}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>{formatDate(item.subscribed_at)}</td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>
                        <span style={{ 
                          padding: '4px 12px', 
                          background: item.status === 'active' ? 'rgba(212, 175, 55, 0.1)' : 'rgba(0,0,0,0.05)',
                          color: item.status === 'active' ? 'var(--accent-gold)' : 'var(--text-secondary)',
                          borderRadius: '0px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'contacts' && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border-light)' }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Name</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Email</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Phone</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Message</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{item.name}</td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{item.email}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>{item.phone || 'N/A'}</td>
                      <td style={{ padding: '16px', fontSize: '14px', maxWidth: '300px' }}>{item.message}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>{formatDate(item.submitted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'appointments' && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border-light)' }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Name</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Email</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Phone</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Date</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Time</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Notes</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Booked At</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{item.name}</td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{item.email}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>{item.phone || 'N/A'}</td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{formatDate(item.date).split(',')[0]}</td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{item.time}</td>
                      <td style={{ padding: '16px', fontSize: '14px', maxWidth: '200px' }}>{item.notes || 'N/A'}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>{formatDate(item.booked_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
